import { useEffect, useState } from 'react';

interface PublicArticleData {
  id: string;
  url: string;
  title: string | null;
  content: string | null;
  excerpt: string | null;
  word_count: number | null;
  source_type: string;
  source_metadata: Record<string, unknown>;
  shared_at: string;
  created_at: string;
  reading_time_minutes: number | null;
  shared_by: string;
}

interface PublicArticleProps {
  shareToken: string;
}

export function PublicArticle({ shareToken }: PublicArticleProps) {
  const [article, setArticle] = useState<PublicArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchArticle();
  }, [shareToken]);

  async function fetchArticle() {
    try {
      setLoading(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-public-article?token=${encodeURIComponent(shareToken)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Article not found');
      }

      const data = await response.json();
      setArticle(data.article);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load article');
    } finally {
      setLoading(false);
    }
  }

  function handleSaveToReadLater() {
    // Open the extension or show install prompt
    // For now, just open the original URL
    if (article?.url) {
      window.open(article.url, '_blank');
    }
  }

  if (loading) {
    return (
      <div className="public-article-loading">
        <div className="loading-spinner" />
        <p>Loading article...</p>
        <style>{publicArticleStyles}</style>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="public-article-error">
        <h1>Article Not Found</h1>
        <p>{error || 'This article may have been made private or deleted.'}</p>
        <a href="/" className="back-link">Go to Read Later</a>
        <style>{publicArticleStyles}</style>
      </div>
    );
  }

  const sourceMetadata = article.source_metadata as {
    siteName?: string;
    byline?: string;
    coverImageUrl?: string;
    faviconUrl?: string;
  };

  return (
    <div className="public-article">
      <header className="public-article-header">
        <div className="header-content">
          <a href="/" className="brand">Read Later</a>
          <button className="save-btn" onClick={handleSaveToReadLater}>
            Save to Read Later
          </button>
        </div>
      </header>

      <main className="public-article-main">
        <article className="article-content">
          {sourceMetadata.coverImageUrl && (
            <img
              src={sourceMetadata.coverImageUrl}
              alt=""
              className="cover-image"
            />
          )}

          <div className="article-meta">
            {sourceMetadata.siteName && (
              <span className="site-name">{sourceMetadata.siteName}</span>
            )}
            {article.reading_time_minutes && (
              <span className="reading-time">{article.reading_time_minutes} min read</span>
            )}
          </div>

          <h1 className="article-title">{article.title || 'Untitled'}</h1>

          {sourceMetadata.byline && (
            <p className="byline">{sourceMetadata.byline}</p>
          )}

          <div className="shared-info">
            Shared by {article.shared_by} on {new Date(article.shared_at).toLocaleDateString()}
          </div>

          {article.content ? (
            <div
              className="article-body"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          ) : article.excerpt ? (
            <div className="article-body">
              <p>{article.excerpt}</p>
              <a href={article.url} target="_blank" rel="noopener noreferrer" className="read-original">
                Read the full article →
              </a>
            </div>
          ) : (
            <div className="article-body">
              <p>No content available.</p>
              <a href={article.url} target="_blank" rel="noopener noreferrer" className="read-original">
                View original article →
              </a>
            </div>
          )}

          <footer className="article-footer">
            <a href={article.url} target="_blank" rel="noopener noreferrer" className="original-link">
              View original at {new URL(article.url).hostname}
            </a>
          </footer>
        </article>

        <div className="cta-banner">
          <h2>Save articles to read later</h2>
          <p>Never lose an interesting article again. Save, highlight, and sync across all your devices.</p>
          <a href="/" className="cta-btn">Get Read Later</a>
        </div>
      </main>

      <style>{publicArticleStyles}</style>
    </div>
  );
}

const publicArticleStyles = `
  .public-article {
    min-height: 100vh;
    background: var(--bg-primary, #0a0a0a);
    color: var(--text-primary, #ffffff);
  }

  .public-article-loading,
  .public-article-error {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    background: var(--bg-primary, #0a0a0a);
    color: var(--text-secondary, #888888);
    padding: 2rem;
    text-align: center;
  }

  .public-article-error h1 {
    color: var(--text-primary, #ffffff);
    font-size: 1.5rem;
    margin: 0;
  }

  .back-link {
    color: var(--accent, #f97316);
    text-decoration: none;
    margin-top: 1rem;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(249, 115, 22, 0.2);
    border-top-color: var(--accent, #f97316);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .public-article-header {
    position: sticky;
    top: 0;
    background: var(--bg-secondary, #1a1a1a);
    border-bottom: 1px solid var(--border, #333);
    padding: 0.75rem 1rem;
    z-index: 100;
  }

  .header-content {
    max-width: 720px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .brand {
    font-weight: 600;
    color: var(--accent, #f97316);
    text-decoration: none;
    font-size: 1rem;
  }

  .save-btn {
    background: var(--accent, #f97316);
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .save-btn:hover {
    opacity: 0.9;
  }

  .public-article-main {
    max-width: 720px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  .article-content {
    background: var(--bg-secondary, #1a1a1a);
    border-radius: 12px;
    overflow: hidden;
  }

  .cover-image {
    width: 100%;
    max-height: 400px;
    object-fit: cover;
  }

  .article-meta {
    padding: 1.5rem 1.5rem 0;
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: var(--text-secondary, #888888);
  }

  .site-name {
    color: var(--accent, #f97316);
  }

  .article-title {
    padding: 0.75rem 1.5rem;
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .byline {
    padding: 0 1.5rem;
    margin: 0;
    font-size: 0.875rem;
    color: var(--text-secondary, #888888);
  }

  .shared-info {
    padding: 0.5rem 1.5rem 1.5rem;
    font-size: 0.75rem;
    color: var(--text-tertiary, #666666);
    border-bottom: 1px solid var(--border, #333);
  }

  .article-body {
    padding: 1.5rem;
    font-size: 1.125rem;
    line-height: 1.8;
    color: var(--text-primary, #e0e0e0);
  }

  .article-body p {
    margin: 0 0 1.5rem;
  }

  .article-body h2, .article-body h3 {
    margin: 2rem 0 1rem;
    font-weight: 600;
  }

  .article-body img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1.5rem 0;
  }

  .article-body a {
    color: var(--accent, #f97316);
    text-decoration: none;
  }

  .article-body a:hover {
    text-decoration: underline;
  }

  .article-body blockquote {
    border-left: 3px solid var(--accent, #f97316);
    margin: 1.5rem 0;
    padding-left: 1rem;
    color: var(--text-secondary, #888888);
    font-style: italic;
  }

  .article-body pre {
    background: var(--bg-tertiary, #2a2a2a);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 0.875rem;
  }

  .article-body code {
    background: var(--bg-tertiary, #2a2a2a);
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    font-size: 0.875em;
  }

  .read-original {
    display: inline-block;
    margin-top: 1rem;
    color: var(--accent, #f97316);
    text-decoration: none;
    font-weight: 500;
  }

  .article-footer {
    padding: 1.5rem;
    border-top: 1px solid var(--border, #333);
  }

  .original-link {
    font-size: 0.875rem;
    color: var(--text-secondary, #888888);
    text-decoration: none;
  }

  .original-link:hover {
    color: var(--accent, #f97316);
  }

  .cta-banner {
    margin-top: 2rem;
    padding: 2rem;
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
    border-radius: 12px;
    text-align: center;
  }

  .cta-banner h2 {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: white;
  }

  .cta-banner p {
    margin: 0 0 1rem;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
  }

  .cta-btn {
    display: inline-block;
    background: white;
    color: #f97316;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.2s;
  }

  .cta-btn:hover {
    transform: scale(1.05);
  }

  @media (max-width: 640px) {
    .public-article-main {
      padding: 1rem;
    }

    .article-title {
      font-size: 1.5rem;
      padding: 0.75rem 1rem;
    }

    .article-meta,
    .byline,
    .shared-info,
    .article-body,
    .article-footer {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    .article-body {
      font-size: 1rem;
    }
  }
`;
